import { QueryOptions, useQueries, useQuery } from '@tanstack/react-query';
import { defaultParseError, durationToMilliseconds, handleParsedError } from '@silo/utils';
import { noop } from 'lodash';
import { useWeb3Context } from '../web3Context';
import { queryDecoratedSilo } from './api';
import { DecoratedSilo, decoratedSiloKey, DecoratedSiloKey } from './types';
import { useSiloConfiguration } from '@silo/config-legacy';
import { parseAllQueryData } from '@silo/query';
import { ChecksumAddress, makeAddressSync } from '@silo/types';
import { useQueryMerklCampaigns } from '../use-query-merkl-campaigns';

interface Options {
  suspense?: boolean;
  useErrorBoundary?: boolean;
  enabled?: boolean;
}

/**
 * @deprecated use granular queries instead
 */
export const useQueryDecoratedSilos = (siloAddresses: ChecksumAddress[] = [], options?: Options) => {
  const { suspense, useErrorBoundary, enabled = true } = { ...options };
  const web3Context = useWeb3Context();
  const { getConfigurationForSilo } = useSiloConfiguration(web3Context?.protocolInstance);

  const { data } = useQueryMerklCampaigns();

  const queries = useQueries<QueryOptions<DecoratedSilo, unknown, DecoratedSilo, DecoratedSiloKey>[]>({
    queries: siloAddresses.map((siloAddress) => {
      const marketAddress = makeAddressSync(siloAddress);

      const siloMerklCampaigns = data
        ? data.filter(
            (campaign) =>
              campaign.marketAddress === marketAddress && campaign.protocolKey === web3Context?.protocolInstance
          )
        : undefined;

      return {
        queryKey: [
          decoratedSiloKey,
          web3Context!,
          siloAddress,
          getConfigurationForSilo(siloAddress),
          siloMerklCampaigns!,
        ],
        queryFn: queryDecoratedSilo,
        enabled: !!web3Context && !!siloMerklCampaigns && enabled,
        onError: (error) => {
          // TODO: implement error handling (not necessarily notifying user)
          const parsedError = defaultParseError(error);
          handleParsedError({
            onWarning: noop,
            onError: noop,
            onUnparsed: noop,
          })(parsedError);
        },
        suspense,
        useErrorBoundary,
        staleTime: durationToMilliseconds({ minutes: 1 }),
        keepPreviousData: true,
      };
    }),
  });

  return { queries, allData: parseAllQueryData(queries) };
};

/**
 * @deprecated use granular queries instead
 *
 * This implementation is copied from the `useQueryDecoratedSilos` hook instead
 * of reusing it because react-query does not currently support suspense with
 * useQueries: https://github.com/TanStack/query/discussions/3422
 */
export const useQueryDecoratedSilo = (siloAddress: ChecksumAddress | null, options?: Options) => {
  const web3Context = useWeb3Context();
  const { getConfigurationForSilo } = useSiloConfiguration(web3Context?.protocolInstance);
  const siloConfig = siloAddress ? getConfigurationForSilo(siloAddress) : null;

  const { data } = useQueryMerklCampaigns();

  const marketAddress = siloAddress ? makeAddressSync(siloAddress) : null;

  const siloMerklCampaigns = data
    ? data.filter(
        (campaign) => campaign.marketAddress === marketAddress && campaign.protocolKey === web3Context?.protocolInstance
      )
    : undefined;

  const queries = useQuery({
    queryKey: [decoratedSiloKey, web3Context!, siloAddress!, siloConfig!, siloMerklCampaigns!],
    queryFn: queryDecoratedSilo,
    enabled: !!web3Context && !!siloAddress && !!siloConfig && !!siloMerklCampaigns && (options?.enabled ?? true),
    onError: (error) => {
      // TODO: implement error handling (not necessarily notifying user)
      const parsedError = defaultParseError(error);
      handleParsedError({
        onWarning: noop,
        onError: noop,
        onUnparsed: noop,
      })(parsedError);
    },
    keepPreviousData: true,
    ...options,
  });

  return queries;
};

export const useQueryDecoratedSiloWithSuspense = (siloAddress: ChecksumAddress | null, enabled = true) => {
  /**
   * Not using actual suspense for now - error boundaries need to be thoroughly placed to use suspense
   */
  const { data } = useQueryDecoratedSilo(siloAddress, { enabled });

  if (!data) {
    throw new Error('data is undefined. Suspense is not working as intended.');
  }

  return data as DecoratedSilo;
};
